import React, { useEffect, useMemo } from 'react';

import { isNil } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Gender } from '@appTypes/enums';
import type { PostParentGuardianInvitationAcceptRequest } from '@appTypes/esb';
import ParentGuardianInvitationImage from '@assets/images/ParentGuardianInvitation.jpg';
import { querySel } from '@location';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  S,
  Spin,
  T,
  allowedPGRelationships,
} from '@modules/shared';
import type { FormComponentProps } from '@modules/shared/components/Form/Form.types';
import RelationshipTypeDropdown from '@modules/shared/components/RelationshipTypeDropdown/RelationshipTypeDropdown';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import { toastService } from '@modules/toasts';
import {
  useGetParentGuardianInvitationDetailsQuery,
  usePostParentGuardianInvitationAcceptMutation,
} from '@modules/youthProfile/api';

import styles from './styles.less';

type ParentGuardianInvitationForm = {
  relationshipTypeId: number;
};

type Props = FormComponentProps<ParentGuardianInvitationForm>;

const ParentGuardianInvitationPage = ({ form }: Props) => {
  const query: { personGuid: string; invitationId: string } =
    useSelector(querySel);

  const { personGuid, invitationId } = query;

  const { data, isFetching } = useGetParentGuardianInvitationDetailsQuery(
    {
      personGuid,
      invitationId: Number(invitationId),
    },
    { skip: isNil(invitationId) || isNil(personGuid) },
  );

  useEffect(() => {
    if (!isNil(data)) {
      form.setFieldsValue({
        relationshipTypeId: String(data.parentReciprocalRelationshipTypeId),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [acceptInvitation, { isLoading }] =
    usePostParentGuardianInvitationAcceptMutation();

  const profile = useMemo(() => {
    const names = {
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      middleName: data?.middleName ?? '',
    };
    return {
      ...names,
      fullName: !isNil(data) ? getPreferredName(names) : '',
      gender: data?.gender ?? Gender.M,
      email: data?.email ?? '',
    };
  }, [data]);

  const scoutInfo = useMemo(() => {
    if (!data) {
      return { name: '', nameAndMid: '' };
    }
    const name = `${data?.scoutFirstNameInitial}. ${data?.scoutLastName}`;
    const nameAndMid = `${name} - MID ${data?.scoutMemberId}`;
    return {
      name,
      nameAndMid,
    };
  }, [data]);

  const handleFinish: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    form.validateFields(async (err, formValues) => {
      const { relationshipTypeId } = formValues;
      if (!err) {
        try {
          const body: PostParentGuardianInvitationAcceptRequest = {
            relationshipTypeId: Number(relationshipTypeId),
            setPrimaryInfo: false,
          };
          await acceptInvitation({
            payload: body,
            personGuid,
            parentRequestId: Number(invitationId),
          }).unwrap();

          toastService.success(
            <FormattedMessage id="youthProfile.parentGuardianInvitation.approveSuccessful" />,
          );
        } catch {
          toastService.error(
            <FormattedMessage id="youthProfile.parentGuardianInvitation.approveError" />,
          );
        }
      }
    });
  };

  return (
    <Form layout="vertical" onSubmit={handleFinish}>
      <Card className={styles.bodyCard}>
        <Row type="flex" align="middle" justify="center" className="mb-2">
          <Col xs={24}>
            <T size="2" bold colored className={styles.title}>
              <FormattedMessage id="youthProfile.parentGuardianInvitation.title" />
            </T>
          </Col>
        </Row>

        <div className={styles.imageContainer}>
          <img alt="Parent guardian" src={ParentGuardianInvitationImage} />
        </div>

        <Row type="flex" align="middle" justify="center" className="mb-2">
          <Col xs={24}>
            <S size="2" bold className={styles.title}>
              <FormattedMessage id="youthProfile.parentGuardianInvitation.subtitle" />
            </S>
          </Col>
        </Row>
        <Spin spinning={isFetching} size="large">
          <Row type="flex" justify="center" align="middle" className="mb-4">
            <Col className={styles.personData} xs={8}>
              <S bold>{profile.fullName}</S>
              <S className="ml-2">{`(${profile.email})`}</S>
            </Col>
          </Row>

          <Row type="flex" align="middle" justify="center" className="mb-4">
            <Col xs={24}>
              <S>
                <FormattedMessage id="youthProfile.parentGuardianInvitation.description" />
              </S>
            </Col>
          </Row>
          <Row type="flex" gutter={24} align="middle" className="mb-2">
            <Col xs={4}>
              <S size="4" bold>
                <FormattedMessage id="youthProfile.parentGuardianInvitation.connectionDropdownLabel" />
              </S>
            </Col>
            <Col xs={12}>
              <RelationshipTypeDropdown
                {...profile}
                form={form}
                recipientPerson={scoutInfo.name}
                required={true}
                noMargin={true}
                floatingLabel={false}
                disabled={isLoading}
                allowedRelationships={allowedPGRelationships}
              />
            </Col>
          </Row>

          <Row type="flex" align="middle" className="mb-2">
            <Col xs={4}>
              <S bold>
                <FormattedMessage id="youthProfile.parentGuardianInvitation.scoutName" />
              </S>
            </Col>
            <Col xs={8}>
              <S>{scoutInfo.nameAndMid}</S>
            </Col>
          </Row>
          <Row type="flex" align="middle" justify="center">
            <Button
              type="primary"
              shape="round"
              size="large"
              htmlType="submit"
              loading={isLoading}
            >
              <FormattedMessage id="shared.accept" />
            </Button>
          </Row>
        </Spin>
      </Card>
    </Form>
  );
};

export default Form.create({})(ParentGuardianInvitationPage);
